import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Check, ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "./ui/button";

const WHATSAPP_LINK = "https://api.whatsapp.com/send/?phone=5508000155555&text&type=phone_number&app_absent=0";

const plans = [
  {
    name: "WhatsApp API",
    focus: "Automação",
    price: "19,90",
    features: ["Número exclusivo para API", "Bots e automação", "Atendimento integrado"],
    featured: false,
  },
  {
    name: "Start 2GB",
    focus: "Entrada",
    price: "29,90",
    features: ["WhatsApp livre", "100 min de ligação", "Dados 2GB"],
    featured: false,
  },
  {
    name: "Start 8GB",
    focus: "Essencial",
    price: "39,90",
    features: ["WhatsApp livre", "100 min de ligação", "60 SMS", "Dados 8GB"],
    featured: false,
  },
  {
    name: "Start 14GB",
    focus: "Crescimento",
    price: "49,90",
    features: ["WhatsApp livre", "Ligações ilimitadas", "Dados 14GB"],
    featured: true,
  },
  {
    name: "Start 21GB",
    focus: "Performance",
    price: "59,90",
    features: ["WhatsApp livre", "Ligações ilimitadas", "Dados de alta velocidade 21GB"],
    featured: false,
  },
  {
    name: "Turbo 39GB",
    focus: "Premium",
    price: "79,90",
    features: ["WhatsApp livre", "Ligações ilimitadas", "Dados 39GB", "SVAs inclusos"],
    featured: false,
  },
];

const PlansSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="planos"
      className="py-20 lg:py-32 bg-brand-charcoal relative"
      data-testid="plans-section"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center max-w-3xl mx-auto mb-12 lg:mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-4 py-2 bg-brand-ruby/10 text-brand-ruby text-sm font-bold tracking-wider uppercase rounded-full mb-4">
            Planos
          </span>
          <h2 className="font-montserrat font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight mb-4">
            Planos flexíveis para{" "}
            <span className="text-brand-ruby">cada fase</span> do seu negócio
          </h2>
        </motion.div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              className={`plan-card p-6 bg-brand-gray/50 rounded-2xl ${
                plan.featured ? "featured" : ""
              }`}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-testid={`plan-card-${index}`}
            >
              {/* Plan Header */}
              <div className="mb-4">
                <span className="text-brand-ruby text-xs font-bold uppercase tracking-wider">
                  {plan.focus}
                </span>
                <h3 className="font-bold text-xl text-white mt-1">{plan.name}</h3>
              </div>

              {/* Price */}
              <div className="mb-6">
                <span className="text-white/60 text-sm">R$</span>
                <span className="font-black text-4xl text-white ml-1">
                  {plan.price}
                </span>
                <span className="text-white/60 text-sm">/mês</span>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-5 h-5 bg-brand-ruby/20 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-brand-ruby" />
                    </div>
                    <span className="text-white/80 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                <Button
                  className={`w-full font-bold ${
                    plan.featured
                      ? "bg-brand-ruby hover:bg-brand-ruby/90 text-white"
                      : "bg-white/10 hover:bg-white/20 text-white"
                  }`}
                  data-testid={`plan-cta-${index}`}
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Contratar
                </Button>
              </a>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <p className="text-white/60 text-sm mb-4">
            Precisa de um plano personalizado para sua empresa?
          </p>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
            <Button
              variant="outline"
              className="border-brand-ruby text-brand-ruby hover:bg-brand-ruby hover:text-white"
              data-testid="custom-plan-btn"
            >
              Falar com Consultor
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default PlansSection;
